import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Textarea,
  Divider,
  useColorModeValue,
  useToast,
  Icon,
} from '@chakra-ui/react'
import { MdCheckCircle, MdWarning } from 'react-icons/md'
import { useApproveCancellation } from 'queries/cancellation.query'

export function ApproveModal({ isOpen, onClose, cancellation }) {
  const [adminNotes, setAdminNotes] = useState('')
  const toast = useToast()
  const approveMutation = useApproveCancellation()

  const cardBg = useColorModeValue('gray.50', 'navy.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')

  if (!cancellation) return null

  const handleApprove = async () => {
    try {
      const res = await approveMutation.mutateAsync({
        id: cancellation.id,
        adminNotes: adminNotes.trim() || undefined,
      })

      toast({
        title: 'Cancellation Approved & Refunded',
        description: res?.message || 'Gateway refund issued and seat released.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      onClose()
      setAdminNotes('')
    } catch (err) {
      toast({
        title: 'Approval Failed',
        description: err?.response?.data?.message || err.message || 'Error processing refund.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} py={4}>
          <HStack spacing={2.5}>
            <Icon as={MdCheckCircle} color="green.500" w="24px" height="24px" />
            <Text fontSize="lg" fontWeight="bold">
              Approve Cancellation & Issue Refund
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            {/* Warning alert */}
            <Box
              p={3.5}
              borderRadius="12px"
              bg="orange.50"
              _dark={{ bg: 'orange.900', color: 'orange.200' }}
              border="1px solid"
              borderColor="orange.200"
              _dark_borderColor="orange.700"
            >
              <HStack align="flex-start" spacing={2.5}>
                <Icon as={MdWarning} color="orange.500" w="20px" height="20px" mt="2px" />
                <Text fontSize="xs" color="orange.800" _dark={{ color: 'orange.200' }} lineHeight="tall">
                  <strong>Notice:</strong> Approving will execute an immediate refund through the original payment gateway
                  (Stripe / PayPal) for the net refundable amount and automatically release the seat to the waitlist queue.
                </Text>
              </HStack>
            </Box>

            {/* Financial Breakdown */}
            <Box p={4} borderRadius="14px" bg={cardBg} border="1px solid" borderColor={borderColor}>
              <VStack spacing={2.5} align="stretch" fontSize="sm">
                <HStack justify="space-between">
                  <Text color="gray.500">Order Number:</Text>
                  <Text fontWeight="bold" fontFamily="mono">
                    {cancellation.order?.order_number || '—'}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Ticket Code:</Text>
                  <Text fontWeight="bold" fontFamily="mono">
                    #{cancellation.ticket?.ticket_code || '—'}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500">Customer:</Text>
                  <Text fontWeight="semibold">
                    {cancellation.customer_name} ({cancellation.customer_email})
                  </Text>
                </HStack>

                <Divider my={1} />

                <HStack justify="space-between">
                  <Text color="gray.500">Original Ticket Price:</Text>
                  <Text fontWeight="semibold">€{Number(cancellation.original_price).toFixed(2)}</Text>
                </HStack>
                <HStack justify="space-between" color="red.500">
                  <Text>Penalty Fee ({cancellation.penalty_percent}%):</Text>
                  <Text fontWeight="semibold">-€{Number(cancellation.penalty_amount).toFixed(2)}</Text>
                </HStack>
                <Divider my={1} />
                <HStack justify="space-between" fontSize="md">
                  <Text fontWeight="bold" color="green.500">
                    Net Refund Amount:
                  </Text>
                  <Text fontWeight="black" color="green.500" fontSize="lg">
                    €{Number(cancellation.refundable_amount).toFixed(2)}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Customer Reason */}
            {cancellation.customer_reason && (
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                  Customer Reason:
                </Text>
                <Text fontSize="xs" fontStyle="italic" p={2.5} bg={cardBg} borderRadius="8px">
                  "{cancellation.customer_reason}"
                </Text>
              </Box>
            )}

            {/* Optional Admin Notes */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Admin Notes / Audit Note (Optional):
              </Text>
              <Textarea
                placeholder="Add any internal reference or notes..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                borderRadius="12px"
                fontSize="xs"
                rows={2}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor} py={3}>
          <Button variant="ghost" mr={3} onClick={onClose} borderRadius="10px">
            Cancel
          </Button>
          <Button
            colorScheme="green"
            borderRadius="10px"
            onClick={handleApprove}
            isLoading={approveMutation.isPending}
            loadingText="Processing Refund..."
            px={6}
          >
            Confirm & Issue Refund
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
